import jsPDF from 'jspdf';

import type { Order, FulfillmentType } from '../types/@types/order';
import type { Table } from '../types/@types/table';
import { formatCurrency } from './format-currency';
import { getPrinterConfig } from '../hooks/usePrinterConfig';

export interface ReceiptLine {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
  notes?: string | null;
}

export interface ReceiptPayment {
  method: string;
  amount: number;
  received?: number;
}

export interface ReceiptData {
  orderId?: string | number;
  orderNumber?: number | null;
  tableLabel?: string;
  fulfillment?: FulfillmentType | string | null;
  customerName?: string;
  paidAt: string;
  items: ReceiptLine[];
  subtotal: number;
  discountAmount?: number;
  total: number;
  payments?: ReceiptPayment[];
  received?: number;
  change?: number;
  isPreReceipt?: boolean;
  isPaid?: boolean;
}

interface BuildReceiptParams {
  order?: Order | null;
  items: ReceiptLine[];
  totals: { subtotal: number; total: number };
  table?: Table | null;
  customerName?: string;
  payments?: ReceiptPayment[];
  discountAmount?: number;
  received?: number;
  change?: number;
  paidAt?: string;
  isPreReceipt?: boolean;
  isPaid?: boolean;
}

export function buildReceiptData({
  order,
  items,
  totals,
  table,
  customerName,
  payments,
  discountAmount,
  received,
  change,
  paidAt,
  isPreReceipt,
  isPaid,
}: BuildReceiptParams): ReceiptData {
  const fulfillment = order?.fulfillmentType ?? null;
  const tableLabel =
    fulfillment === 'TAKEOUT'
      ? 'Para llevar'
      : table?.number !== undefined && table?.number !== null
        ? `Mesa ${table.number}`
        : undefined;

  const derivedDiscount = discountAmount ?? (Number(order?.discount_amount) > 0 ? Number(order?.discount_amount) : (totals.subtotal > totals.total ? totals.subtotal - totals.total : undefined));

  const orderIsPaid = isPaid ?? ((order as any)?.paymentStatus === 'PAID' || (order as any)?.payment_status === 'PAID' || Boolean(payments && payments.length > 0) || !isPreReceipt);

  return {
    orderId: order?.id,
    orderNumber: order?.orderSequence ?? null,
    tableLabel,
    fulfillment,
    customerName: customerName?.trim() || order?.customerName || undefined,
    paidAt: paidAt ?? new Date().toISOString(),
    items,
    subtotal: totals.subtotal,
    discountAmount: derivedDiscount,
    total: totals.total,
    payments: payments && payments.length > 0 ? payments : undefined,
    received,
    change,
    isPreReceipt: Boolean(isPreReceipt),
    isPaid: orderIsPaid,
  };
}

export function downloadReceiptPdf(data: ReceiptData) {
  const W = getPrinterConfig().paperWidth;     // ancho del papel en mm
  const RIGHT = W - 4;                          // borde derecho con margen
  const doc = new jsPDF({ unit: 'mm', format: [W, 200] });
  if (W <= 58) doc.setFontSize(8);
  let cursorY = 10;

  const centerText = (text: string) => {
    doc.text(text, W / 2, cursorY, { align: 'center' });
    cursorY += 6;
  };

  centerText(data.isPreReceipt ? '*** PRE-CUENTA ***' : 'Recibo de pago');
  if (data.isPreReceipt) {
    doc.setFontSize(7);
    centerText('(No válido como factura)');
    doc.setFontSize(W <= 58 ? 8 : 9);
  }

  const isTakeaway = data.fulfillment === 'TAKEOUT' || data.tableLabel === 'Para llevar' || !data.tableLabel;
  if (isTakeaway) {
    doc.setFontSize(9);
    if (data.isPaid) {
      centerText('*** PEDIDO PARA LLEVAR: YA PAGADO ***');
    } else {
      centerText('⚠️ PARA LLEVAR: FALTA POR COBRAR ⚠️');
      centerText(`(Total a cobrar: ${formatCurrency(data.total)})`);
    }
    doc.setFontSize(W <= 58 ? 8 : 9);
  }

  centerText(data.tableLabel || 'Para llevar');
  if (data.orderNumber) {
    centerText(`Orden #${data.orderNumber}`);
  }

  cursorY += 2;
  doc.setFontSize(9);
  doc.text(`Fecha: ${new Date(data.paidAt).toLocaleString('es-AR')}`, 4, cursorY);
  cursorY += 5;
  if (data.customerName) {
    doc.text(`Cliente: ${data.customerName}`, 4, cursorY);
    cursorY += 5;
  }

  doc.text('Items:', 4, cursorY);
  cursorY += 5;
  data.items.forEach((item) => {
    doc.text(`${item.quantity} x ${item.name}`, 4, cursorY);
    doc.text(formatCurrency(item.total), RIGHT, cursorY, { align: 'right' });
    cursorY += 5;
    if (item.notes) {
      doc.text(`   ${item.notes}`, 4, cursorY);
      cursorY += 4;
    }
  });

  cursorY += 2;
  doc.text(`Subtotal: ${formatCurrency(data.subtotal)}`, 4, cursorY);
  cursorY += 5;
  if (typeof data.discountAmount === 'number' && data.discountAmount > 0) {
    doc.text(`Descuento: - ${formatCurrency(data.discountAmount)}`, 4, cursorY);
    cursorY += 5;
  }
  doc.text(`${data.isPreReceipt ? 'TOTAL A PAGAR' : 'Total'}: ${formatCurrency(data.total)}`, 4, cursorY);
  cursorY += 6;

  if (data.isPreReceipt) {
    doc.setFontSize(8);
    centerText('*** PENDIENTE DE PAGO ***');
    centerText('Presente este ticket en caja para pagar');
  } else if (data.payments && data.payments.length > 0) {
    doc.text('Forma de pago:', 4, cursorY);
    cursorY += 5;
    data.payments.forEach((p) => {
      doc.text(`  ${p.method}:`, 4, cursorY);
      doc.text(formatCurrency(p.amount), RIGHT, cursorY, { align: 'right' });
      cursorY += 5;
    });
    if (typeof data.received === 'number' && data.received > data.total) {
      doc.text(`Recibido: ${formatCurrency(data.received)}`, 4, cursorY);
      cursorY += 5;
    }
    if (typeof data.change === 'number' && data.change > 0) {
      doc.text(`Cambio: ${formatCurrency(data.change)}`, 4, cursorY);
      cursorY += 5;
    }
  } else {
    if (typeof data.received === 'number') {
      doc.text(`Recibido: ${formatCurrency(data.received)}`, 4, cursorY);
      cursorY += 5;
    }
    if (typeof data.change === 'number' && data.change > 0) {
      doc.text(`Cambio: ${formatCurrency(data.change)}`, 4, cursorY);
      cursorY += 5;
    }
  }

  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return;
  }

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${data.isPreReceipt ? 'precuenta' : 'recibo'}-${data.orderNumber ?? 'mesa'}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export function triggerReceiptPrint(data: ReceiptData) {
  if (typeof window !== 'undefined' && typeof window.print === 'function') {
    window.print();
    return 'print';
  }
  downloadReceiptPdf(data);
  return 'pdf';
}

export function shouldPromptReceiptPrint(message = '¿Desea imprimir el recibo?') {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
    return false;
  }
  return window.confirm(message);
}
